import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
} from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  // Communication & Contact - Essential for chat widgets
  MessageSquare, MessagesSquare, MessageCircle, Mail, Phone, PhoneCall, Video, VideoOff,
  Send, Share, Share2, Reply, Forward, AtSign, Headphones, Mic, MicOff,
  PhoneIncoming, PhoneOutgoing, PhoneMissed, MessageSquareText, MessageSquareMore,
  Voicemail, PhoneForwarded, MessageSquareReply, MessageSquareDot,
  
  // Interface & Navigation - Core UI elements
  Menu, X, Plus, Minus, Search, Filter, Settings, MoreHorizontal, MoreVertical,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ChevronsUpDown,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Home, ExternalLink,
  Maximize, Minimize, Expand, Shrink, CornerDownLeft, CornerUpRight,
  
  // Actions & Tools - Common widget actions
  Edit, Edit2, Edit3, Trash, Trash2, Copy, Save, Download, Upload, RefreshCw,
  RotateCcw, RotateCw, Undo, Redo, Scissors, Paperclip, Link, Unlink,
  Archive, Pin, PinOff,
  
  // Files & Media - Document and media handling
  File, FileText, Folder, FolderOpen, Image, Camera, Play, Pause, Square,
  SkipBack, SkipForward, Volume2, VolumeX, Music, Film, FileImage,
  FileDown, FileUp, FilePlus, FileCheck, Printer, ScanLine,
  
  // Status & Feedback - User feedback and notifications
  Check, CheckCircle, AlertCircle, AlertTriangle, Info, 
  HelpCircle, Bell, BellOff, Eye, EyeOff, ThumbsUp, ThumbsDown, Heart, 
  HeartOff, Star, StarOff, Award, Trophy, Medal, Shield, ShieldCheck,
  CheckCircle2, XCircle, AlertOctagon, BellRing, BellDot,
  
  // User & Social - User management
  User, Users, UserPlus, UserMinus, UserCheck, Crown, Contact, Smile,
  Frown, Meh, UserCircle, Bot, BotMessageSquare, UserCog, Users2,
  
  // Help & Support - Essential for support widgets
  LifeBuoy, FileQuestion, MessageCircleQuestion,
  Headset, CircleHelp,
  
  // Time & Location - Scheduling and location
  Clock, Calendar, MapPin, Globe, Compass, Navigation, Flag,
  Tag, Hash, Map, CalendarDays, Clock3, Timer,
  
  // Technology & Tools - Technical support
  Smartphone, Laptop, Monitor, Wifi, WifiOff, Cloud, CloudOff, Database,
  Server, Cpu, HardDrive, Usb, Bluetooth, Signal, Battery, ZapOff,
  Terminal, Code, Bug, Settings2,
  
  // Business & Finance - Business context
  Building, Building2, Store, ShoppingCart, CreditCard, DollarSign,
  TrendingUp, TrendingDown, Activity, Package, Briefcase,
  
  // Security & Privacy - Important for trust
  Lock, Unlock, Key, ShieldAlert, ShieldX, 
  Fingerprint, Scan, QrCode,
  
  // Weather & Nature - General purpose
  Sun, Moon, CloudRain, Snowflake, Umbrella, 
  TreePine, Flower, Leaf,
  
  // Miscellaneous - Additional useful icons
  Gift, Lightbulb, Coffee, Pizza, Car, Plane, Rocket, Gamepad2,
  Palette, Brush, Hammer, Sliders, Puzzle,
  Sparkles, Crosshair, Focus, Layers
} from "lucide-react";

// All available icons with their components
const AVAILABLE_ICONS = [
  // Communication & Contact - Essential for chat widgets
  { name: 'message-square', component: MessageSquare },
  { name: 'messages-square', component: MessagesSquare },
  { name: 'message-circle', component: MessageCircle },
  { name: 'message-square-text', component: MessageSquareText },
  { name: 'message-square-more', component: MessageSquareMore },
  { name: 'message-square-reply', component: MessageSquareReply },
  { name: 'message-square-dot', component: MessageSquareDot },
  { name: 'message-circle-question', component: MessageCircleQuestion },
  { name: 'mail', component: Mail },
  { name: 'phone', component: Phone },
  { name: 'phone-call', component: PhoneCall },
  { name: 'phone-incoming', component: PhoneIncoming },
  { name: 'phone-outgoing', component: PhoneOutgoing },
  { name: 'phone-missed', component: PhoneMissed },
  { name: 'phone-forwarded', component: PhoneForwarded },
  { name: 'voicemail', component: Voicemail },
  { name: 'video', component: Video },
  { name: 'video-off', component: VideoOff },
  { name: 'send', component: Send },
  { name: 'share', component: Share },
  { name: 'share-2', component: Share2 },
  { name: 'reply', component: Reply },
  { name: 'forward', component: Forward },
  { name: 'at-sign', component: AtSign },
  { name: 'headphones', component: Headphones },
  { name: 'headset', component: Headset },
  { name: 'mic', component: Mic },
  { name: 'mic-off', component: MicOff },
  
  // Interface & Navigation
  { name: 'menu', component: Menu },
  { name: 'x', component: X },
  { name: 'plus', component: Plus },
  { name: 'minus', component: Minus },
  { name: 'search', component: Search },
  { name: 'filter', component: Filter },
  { name: 'settings', component: Settings },
  { name: 'more-horizontal', component: MoreHorizontal },
  { name: 'more-vertical', component: MoreVertical },
  { name: 'chevron-down', component: ChevronDown },
  { name: 'chevron-up', component: ChevronUp },
  { name: 'chevron-left', component: ChevronLeft },
  { name: 'chevron-right', component: ChevronRight },
  { name: 'chevrons-up-down', component: ChevronsUpDown },
  { name: 'arrow-up', component: ArrowUp },
  { name: 'arrow-down', component: ArrowDown },
  { name: 'arrow-left', component: ArrowLeft },
  { name: 'arrow-right', component: ArrowRight },
  { name: 'home', component: Home },
  { name: 'external-link', component: ExternalLink },
  { name: 'settings-2', component: Settings2 },
  { name: 'maximize', component: Maximize },
  { name: 'minimize', component: Minimize },
  { name: 'expand', component: Expand },
  { name: 'shrink', component: Shrink },
  { name: 'corner-down-left', component: CornerDownLeft },
  { name: 'corner-up-right', component: CornerUpRight },
  
  // Actions & Tools
  { name: 'edit', component: Edit },
  { name: 'edit-2', component: Edit2 },
  { name: 'edit-3', component: Edit3 },
  { name: 'trash', component: Trash },
  { name: 'trash-2', component: Trash2 },
  { name: 'copy', component: Copy },
  { name: 'save', component: Save },
  { name: 'download', component: Download },
  { name: 'upload', component: Upload },
  { name: 'refresh-cw', component: RefreshCw },
  { name: 'rotate-ccw', component: RotateCcw },
  { name: 'rotate-cw', component: RotateCw },
  { name: 'undo', component: Undo },
  { name: 'redo', component: Redo },
  { name: 'scissors', component: Scissors },
  { name: 'paperclip', component: Paperclip },
  { name: 'link', component: Link },
  { name: 'unlink', component: Unlink },
  { name: 'archive', component: Archive },
  { name: 'pin', component: Pin },
  { name: 'pin-off', component: PinOff },
  
  // Files & Media
  { name: 'file', component: File },
  { name: 'file-text', component: FileText },
  { name: 'folder', component: Folder },
  { name: 'folder-open', component: FolderOpen },
  { name: 'image', component: Image },
  { name: 'camera', component: Camera },
  { name: 'play', component: Play },
  { name: 'pause', component: Pause },
  { name: 'square', component: Square },
  { name: 'skip-back', component: SkipBack },
  { name: 'skip-forward', component: SkipForward },
  { name: 'volume-2', component: Volume2 },
  { name: 'volume-x', component: VolumeX },
  { name: 'music', component: Music },
  { name: 'film', component: Film },
  { name: 'file-image', component: FileImage },
  { name: 'file-down', component: FileDown },
  { name: 'file-up', component: FileUp },
  { name: 'file-plus', component: FilePlus },
  { name: 'file-check', component: FileCheck },
  { name: 'file-question', component: FileQuestion },
  { name: 'printer', component: Printer },
  { name: 'scan-line', component: ScanLine },
  
  // Status & Feedback
  { name: 'check', component: Check },
  { name: 'check-circle', component: CheckCircle },
  { name: 'alert-circle', component: AlertCircle },
  { name: 'alert-triangle', component: AlertTriangle },
  { name: 'info', component: Info },
  { name: 'help-circle', component: HelpCircle },
  { name: 'bell', component: Bell },
  { name: 'bell-off', component: BellOff },
  { name: 'eye', component: Eye },
  { name: 'eye-off', component: EyeOff },
  { name: 'thumbs-up', component: ThumbsUp },
  { name: 'thumbs-down', component: ThumbsDown },
  { name: 'heart', component: Heart },
  { name: 'heart-off', component: HeartOff },
  { name: 'star', component: Star },
  { name: 'star-off', component: StarOff },
  { name: 'award', component: Award },
  { name: 'trophy', component: Trophy },
  { name: 'medal', component: Medal },
  { name: 'shield', component: Shield },
  { name: 'shield-check', component: ShieldCheck },
  { name: 'check-circle-2', component: CheckCircle2 },
  { name: 'x-circle', component: XCircle },
  { name: 'alert-octagon', component: AlertOctagon },
  { name: 'bell-ring', component: BellRing },
  { name: 'bell-dot', component: BellDot },
  
  // User & Social
  { name: 'user', component: User },
  { name: 'users', component: Users },
  { name: 'user-plus', component: UserPlus },
  { name: 'user-minus', component: UserMinus },
  { name: 'user-check', component: UserCheck },
  { name: 'crown', component: Crown },
  { name: 'contact', component: Contact },
  { name: 'smile', component: Smile },
  { name: 'frown', component: Frown },
  { name: 'meh', component: Meh },
  { name: 'user-circle', component: UserCircle },
  { name: 'bot', component: Bot },
  { name: 'bot-message-square', component: BotMessageSquare },
  { name: 'user-cog', component: UserCog },
  { name: 'users-2', component: Users2 },
  
  // Help & Support - Essential for support widgets
  { name: 'life-buoy', component: LifeBuoy },
  { name: 'circle-help', component: CircleHelp },
  { name: 'file-question', component: FileQuestion },
  { name: 'message-circle-question', component: MessageCircleQuestion },
  
  // Time & Location
  { name: 'clock', component: Clock },
  { name: 'calendar', component: Calendar },
  { name: 'map-pin', component: MapPin },
  { name: 'globe', component: Globe },
  { name: 'compass', component: Compass },
  { name: 'navigation', component: Navigation },
  { name: 'flag', component: Flag },
  { name: 'tag', component: Tag },
  { name: 'hash', component: Hash },
  { name: 'map', component: Map },
  { name: 'calendar-days', component: CalendarDays },
  { name: 'clock-3', component: Clock3 },
  { name: 'timer', component: Timer },
  
  // Technology & Tools
  { name: 'smartphone', component: Smartphone },
  { name: 'laptop', component: Laptop },
  { name: 'monitor', component: Monitor },
  { name: 'wifi', component: Wifi },
  { name: 'wifi-off', component: WifiOff },
  { name: 'cloud', component: Cloud },
  { name: 'cloud-off', component: CloudOff },
  { name: 'database', component: Database },
  { name: 'server', component: Server },
  { name: 'cpu', component: Cpu },
  { name: 'hard-drive', component: HardDrive },
  { name: 'usb', component: Usb },
  { name: 'bluetooth', component: Bluetooth },
  { name: 'signal', component: Signal },
  { name: 'battery', component: Battery },
  { name: 'zap-off', component: ZapOff },
  { name: 'terminal', component: Terminal },
  { name: 'code', component: Code },
  { name: 'bug', component: Bug },
  
  // Business & Finance
  { name: 'building', component: Building },
  { name: 'building-2', component: Building2 },
  { name: 'store', component: Store },
  { name: 'shopping-cart', component: ShoppingCart },
  { name: 'credit-card', component: CreditCard },
  { name: 'dollar-sign', component: DollarSign },
  { name: 'trending-up', component: TrendingUp },
  { name: 'trending-down', component: TrendingDown },
  { name: 'activity', component: Activity },
  { name: 'package', component: Package },
  { name: 'briefcase', component: Briefcase },
  
  // Security & Privacy
  { name: 'lock', component: Lock },
  { name: 'unlock', component: Unlock },
  { name: 'key', component: Key },
  { name: 'shield-alert', component: ShieldAlert },
  { name: 'shield-x', component: ShieldX },
  { name: 'fingerprint', component: Fingerprint },
  { name: 'scan', component: Scan },
  { name: 'qr-code', component: QrCode },
  
  // Weather & Nature
  { name: 'sun', component: Sun },
  { name: 'moon', component: Moon },
  { name: 'cloud-rain', component: CloudRain },
  { name: 'snowflake', component: Snowflake },
  { name: 'umbrella', component: Umbrella },
  { name: 'tree-pine', component: TreePine },
  { name: 'flower', component: Flower },
  { name: 'leaf', component: Leaf },
  
  // Miscellaneous
  { name: 'gift', component: Gift },
  { name: 'lightbulb', component: Lightbulb },
  { name: 'coffee', component: Coffee },
  { name: 'pizza', component: Pizza },
  { name: 'car', component: Car },
  { name: 'plane', component: Plane },
  { name: 'rocket', component: Rocket },
  { name: 'gamepad-2', component: Gamepad2 },
  { name: 'palette', component: Palette },
  { name: 'brush', component: Brush },
  { name: 'hammer', component: Hammer },
  { name: 'sliders', component: Sliders },
  { name: 'puzzle', component: Puzzle },
  { name: 'sparkles', component: Sparkles },
  { name: 'crosshair', component: Crosshair },
  { name: 'focus', component: Focus },
  { name: 'layers', component: Layers }
];

interface IconPickerProps {
  value?: string;
  onChange: (iconName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function IconPicker({
  value,
  onChange,
  placeholder = "Select an icon",
  disabled = false,
  className
}: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredIcons = useMemo(() => {
    if (!search) {
      return AVAILABLE_ICONS;
    }
    
    return AVAILABLE_ICONS.filter(icon =>
      icon.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  const selectedIcon = value ? AVAILABLE_ICONS.find(icon => icon.name === value) : null;
  const SelectedIconComponent = selectedIcon?.component;

  const handleSelect = (iconName: string) => {
    onChange(iconName);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-start h-9 px-3 font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          {SelectedIconComponent ? (
            <div className="flex items-center gap-2">
              <SelectedIconComponent className="h-4 w-4" />
              <span>{selectedIcon.name}</span>
            </div>
          ) : (
            <span>{placeholder}</span>
          )}
           <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="flex flex-col">
          <Command>
            <CommandInput
              placeholder="Search icons..."
              value={search}
              onValueChange={setSearch}
              className="border-b"
            />
          </Command>
          <ScrollArea className="h-96 w-full">
            <div className="p-1">
              {filteredIcons.length === 0 ? (
                <div className="flex items-center justify-center h-20 text-sm text-muted-foreground">
                  No icons found.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 p-2">
                  {filteredIcons.map((icon) => {
                    const IconComponent = icon.component;
                    return (
                      <button
                        key={icon.name}
                        onClick={() => handleSelect(icon.name)}
                        className="flex flex-col items-center justify-center p-3 h-20 cursor-pointer hover:bg-accent rounded-md border border-transparent hover:border-border transition-colors"
                      >
                        <IconComponent className="h-6 w-6 mb-1" />
                        <span className="text-xs text-center w-full leading-tight break-words">
                          {icon.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Icon preview component for showing selected icons
interface IconPreviewProps {
  iconName?: string;
  size?: number;
  className?: string;
}

export function IconPreview({ iconName, size = 16, className }: IconPreviewProps) {
  if (!iconName) return null;
  
  const icon = AVAILABLE_ICONS.find(i => i.name === iconName);
  
  if (!icon) return null;
  
  const IconComponent = icon.component;
  return <IconComponent className={cn(`h-${size} w-${size}`, className)} />;
}
